// @flow
import React, {useState} from 'react';
import type {ComponentType, Node} from 'react';
import {
  Button,
  Icon,
  IconNames,
  Menu,
  MenuItem,
} from '@wellstone-solutions/web';
import {Theme} from '@wellstone-solutions/common';
import {observer} from 'mobx-react';
import {useStores} from 'hooks/useStores';
import {useResourcesContext} from '../../contexts';
import {DISTANCE_OPTIONS} from '../../constants';

export const DistanceSelect: ComponentType<{}> = observer((): Node => {
  const {distance, setDistance} = useResourcesContext();
  const {locationStore} = useStores();
  const [anchorEl, setAnchorEl] = useState(null);

  // Match the dataloader's logic: distance only applies when we have a
  // city-level (or more specific) location. Hide the button for
  // state-only searches, which intentionally remain unbounded.
  const {city} = locationStore.currentLocation || {};
  if (!city) {
    return null;
  }

  const open = Boolean(anchorEl);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (miles: number) => {
    setDistance(miles);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        data-testid="distance-select-button"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'distance-select-menu' : undefined}
        onClick={handleOpen}
        endIcon={<Icon name={IconNames.NavArrowDown} size={18} />}
        sx={styles.button}>
        {`Within ${distance} miles`}
      </Button>
      <Menu
        id="distance-select-menu"
        data-testid="distance-select-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{list: {'aria-labelledby': 'distance-select-button'}}}>
        {DISTANCE_OPTIONS.map((miles) => (
          <MenuItem
            key={miles}
            selected={miles === distance}
            onClick={() => handleSelect(miles)}>
            {`Within ${miles} miles`}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

const styles = {
  button: {
    backgroundColor: Theme.colorPalette.primary4,
    color: Theme.colorPalette.primary,
    borderRadius: 999,
    paddingX: 1.5,
    paddingY: 0.5,
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: Theme.colorPalette.primary4,
      filter: 'brightness(0.96)',
    },
  },
};
